"use client";

import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Search, MapPin, Banknote, Phone, Mail, FileText, Upload, X } from "lucide-react";
import * as Yup from "yup";
import TextInput from "../common/FormFields/TextInput.jsx";
import TextAreaInput from "../common/FormFields/TextareaInput.jsx";
import NewCustomAsyncSelectDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../common/useScrollLoadMore";
import {
  getJobsByStatus,
  getJobsForStudent,
  getAppliedJobs,
  getMissedJobs,
  getStudentApplicationInfo,
  getJobById,
} from "../../../services/JobsService.js";
import {
  getCoursesFromV1,
  getDepartmentsV2,
  getBranchesV2,
} from "../../../services/CourseModule";
import {
  submitJobApplicationData,
} from "../../../services/JobApplicationService.js";
import { getAllStudentsFromStudentsAPI } from "../../../services/StudentService";
import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import { safeHtml } from '../../../utils/safeHtml';
import "../Trainer/TrainerDashboard.css";
import "./JobSection.css";
import { Formik, Form, ErrorMessage } from "formik";
import { useAuth } from "../../../context/AuthContext";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import { onPageRefresh } from "../../layouts/pageRefresh";
import { Skeleton, SkeletonGroup } from "../common/Skeleton/Skeleton.jsx";
import { silenceFor } from "../common/Loader/busy";

// Constants
/* 25 / 50 / 100, chosen from the control beside the search box. */
const PAGE_SIZE_CHOICES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const studentJobCache = new Map();
// Outlives the component; the topbar's refresh must not be served from it.
onPageRefresh(() => studentJobCache.clear());


export default function JobListings() {
  const { user } = useAuth();
  // Tab and modal states
  const [selectedTab, setSelectedTab] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [showJDModal, setShowJDModal] = useState(false);
  const [selectedJobForJD, setSelectedJobForJD] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Job management states with pagination
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(DEFAULT_PAGE_SIZE);
  /* Set just before a scroll-triggered fetch so the response is appended
     rather than swapped in. */
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list -- the sentinel, in the flow of the page.
  const listEndRef = useRef(null);
  const [totalJobs, setTotalJobs] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Job applications state - only for admin
  const [jobApplications, setJobApplications] = useState([]);
  // Course dropdown states
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseLoading, setCourseLoading] = useState(false);
  const [coursePage, setCoursePage] = useState(1);
  const [courseSearch, setCourseSearch] = useState("");
  const [courseHasMore, setCourseHasMore] = useState(true);

  // Department dropdown states
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptPage, setDeptPage] = useState(1);
  const [deptSearch, setDeptSearch] = useState("");
  const [deptHasMore, setDeptHasMore] = useState(true);

  // Branch dropdown states
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchPage, setBranchPage] = useState(1);
  const [branchSearch, setBranchSearch] = useState("");
  const [branchHasMore, setBranchHasMore] = useState(true);

  // Student prefilled data
  const [prefilledFormData, setPrefilledFormData] = useState({
    tenthMarks: "",
    twelfthMarks: "",
    departmentIds: [], // Changed to array
    branchIds: [], // Changed to array
    currentAddress: "",
    course1: "",
    course2: "",
    course3: "",
  });

  // User state
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentEmail, setSelectedStudentEmail] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [currentStudentEmail, setCurrentStudentEmail] = useState("");

  // Student dropdown states
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentOptionsLoading, setStudentOptionsLoading] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const isFetchingStudents = useRef(false);
  const searchDebounceRef = useRef(null);

  const [prefilledStudentData, setPrefilledStudentData] = useState({
    studentName: "",
    studentEmail: "",
    studentPhoneNumber: "",
    studentDepartments: [], // Store multiple departments from API
    studentBranches: [], // Store multiple branches from API
  });

  // Refs for preventing duplicate calls
  const initializationRef = useRef(false);
  const jobsFetchRef = useRef(false);
  const modalDataFetchedRef = useRef(false);
  const studentInfoFetchedRef = useRef(false);
  const lastSearchRef = useRef("");

  // Check if user is admin
  const checkIfAdmin = (userData) => {
    return userData && userData.isSuperAdmin;
  };

  // Generate cache key for student view
  const getStudentCacheKey = (tab, page, search, studentEmail) => {
    return `student_${tab}_${page}_${search}_${studentEmail}`;
  };

  // Clear student cache
  const clearStudentCache = () => {
    studentJobCache.clear();
  };

  // Debounced search for job search bar
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 700);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch courses with pagination
  const fetchCourses = useCallback(
    async (append = false, search = "", page = 1) => {
      if (courseLoading) return;

      setCourseLoading(true);
      try {
        const limit = 20;
        const res = await getCoursesFromV1(limit, page, search);
        const coursesData = res.data || [];
        const pagination = res.pagination || {};
        const newOptions = coursesData.map((course) => ({
          value: course.id,
          label: course.name,
        }));

        if (append) {
          setCourseOptions((prev) => {
            const filteredNew = newOptions.filter(
              (opt) => !prev.some((p) => p.value === opt.value),
            );
            return [...prev, ...filteredNew];
          });
        } else {
          setCourseOptions(newOptions);
        }

        const hasMore =
          pagination.hasNext || page * limit < (pagination.total || 0);
        setCourseHasMore(hasMore);
        setCoursePage(page);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Failed to load courses");
        if (!append) setCourseOptions([]);
        setCourseHasMore(false);
      } finally {
        setCourseLoading(false);
      }
    },
    [courseLoading],
  );

  // Fetch departments with pagination
  const fetchDepartments = useCallback(
    async (append = false, search = "", page = 1) => {
      if (deptLoading) return;

      setDeptLoading(true);
      try {
        const limit = 20;
        const res = await getDepartmentsV2(limit, page, search);

        const deptsData = res.data || [];
        const pagination = res.pagination || {};
        const newOptions = deptsData.map((dept) => ({
          value: dept.id,
          label: dept.name,
        }));

        if (append) {
          setDeptOptions((prev) => {
            const filteredNew = newOptions.filter(
              (opt) => !prev.some((p) => p.value === opt.value),
            );
            return [...prev, ...filteredNew];
          });
        } else {
          setDeptOptions(newOptions);
        }

        const hasMore =
          pagination.hasNext || page * limit < (pagination.total || 0);
        setDeptHasMore(hasMore);
        setDeptPage(page);
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments");
        if (!append) setDeptOptions([]);
        setDeptHasMore(false);
      } finally {
        setDeptLoading(false);
      }
    },
    [deptLoading],
  );

  // Fetch branches with pagination
  const fetchBranches = useCallback(
    async (append = false, search = "", page = 1) => {
      if (branchLoading) return;

      setBranchLoading(true);
      try {
        const limit = 20;
        const res = await getBranchesV2(limit, page, search);

        const branchesData = res.data || [];
        const pagination = res.pagination || {};

        const newOptions = branchesData.map((branch) => ({
          value: branch.id,
          label: branch.name,
        }));

        if (append) {
          setBranchOptions((prev) => {
            const filteredNew = newOptions.filter(
              (opt) => !prev.some((p) => p.value === opt.value),
            );
            return [...prev, ...filteredNew];
          });
        } else {
          setBranchOptions(newOptions);
        }

        const hasMore =
          pagination.hasNext || page * limit < (pagination.total || 0);
        setBranchHasMore(hasMore);
        setBranchPage(page);
      } catch (error) {
        console.error("Error fetching branches:", error);
        toast.error("Failed to load branches");
        if (!append) setBranchOptions([]);
        setBranchHasMore(false);
      } finally {
        setBranchLoading(false);
      }
    },
    [branchLoading],
  );

  // Fetch students from API with pagination
  const fetchStudents = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingStudents.current = true;
      const response = await getAllStudentsFromStudentsAPI(20, pageNum, search);
      const students = response.data || [];
      const hasMoreData = students.length === 20;
      return { students, hasMore: hasMoreData };
    } catch (err) {
      console.error("Error fetching students:", err);
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudents.current = false;
    }
  }, []);

  // Load more students for dropdown
  const loadMoreStudents = useCallback(async () => {
    if (studentOptionsLoading || !hasMoreStudents || isFetchingStudents.current)
      return;
    setStudentOptionsLoading(true);
    const nextPage = studentPage + 1;
    const { students, hasMore: more } = await fetchStudents(
      studentSearchTerm,
      nextPage,
    );
    if (students.length > 0) {
      const newOptions = students.map((s) => ({
        value: s.email,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));
      setStudentOptions((prev) => [...prev, ...newOptions]);
      setStudentPage(nextPage);
      setHasMoreStudents(more);
    } else {
      setHasMoreStudents(false);
    }
    setStudentOptionsLoading(false);
  }, [
    studentOptionsLoading,
    hasMoreStudents,
    studentPage,
    studentSearchTerm,
    fetchStudents,
  ]);

  // Debounced search handler for student dropdown
  const handleStudentSearch = useCallback(
    (searchValue) => {
      setStudentSearchTerm(searchValue);
      setStudentPage(1);
      setHasMoreStudents(true);
      setStudentOptionsLoading(true);

      const timeoutId = setTimeout(async () => {
        const { students, hasMore: more } = await fetchStudents(searchValue, 1);
        const newOptions = students.map((s) => ({
          value: s.email,
          label: `${s.name} | ${s.email}`,
          student: s,
        }));
        setStudentOptions(newOptions);
        setHasMoreStudents(more);
        setStudentOptionsLoading(false);
      }, 700);

      return () => clearTimeout(timeoutId);
    },
    [fetchStudents],
  );

  // Initial load of students when dropdown opens
  const handleStudentMenuOpen = useCallback(async () => {
    if (studentOptions.length === 0 && !studentOptionsLoading) {
      setStudentOptionsLoading(true);
      setStudentPage(1);
      setStudentSearchTerm("");
      const { students, hasMore: more } = await fetchStudents("", 1);
      const newOptions = students.map((s) => ({
        value: s.email,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));
      setStudentOptions(newOptions);
      setHasMoreStudents(more);
      setStudentOptionsLoading(false);
    }
  }, [studentOptions.length, studentOptionsLoading, fetchStudents]);

  // Handle student selection for admin
  const handleStudentSelection = async (selectedOptions) => {
    const isDeselected = !selectedOptions || selectedOptions.length === 0;

    if (isDeselected) {
      setSelectedStudentId(null);
      setSelectedStudentEmail(null);
      setSelectedStudentName("");
      setPrefilledStudentData({
        studentName: "",
        studentEmail: "",
        studentPhoneNumber: "",
        studentDepartments: [],
        studentBranches: [],
      });
      setPrefilledFormData({
        tenthMarks: "",
        twelfthMarks: "",
        departmentIds: [],
        branchIds: [],
        currentAddress: "",
        course1: "",
        course2: "",
        course3: "",
      });

      clearStudentCache();
      setCurrentPage(1);
      jobsFetchRef.current = false;
      await fetchJobsByTab(selectedTab, 1, debouncedSearchTerm, null);
      return;
    }

    const selectedOption = selectedOptions[0];
    if (!selectedOption) return;

    const { value: email, label, student } = selectedOption;

    const studentChanged = selectedStudentEmail !== email;

    setSelectedStudentId(student.id);
    setSelectedStudentEmail(email);
    setSelectedStudentName(label);

    setPrefilledStudentData({
      studentName: student.name,
      studentEmail: email,
      studentPhoneNumber: student.phone || "",
      studentDepartments: [],
      studentBranches: [],
    });

    if (studentChanged && selectedStudentEmail) {
      clearStudentCache();
    }

    setCurrentPage(1);
    jobsFetchRef.current = false;
    await fetchJobsByTab(selectedTab, 1, debouncedSearchTerm, email);

    // Fetch applications for this student to show "Applied" status
    try {
      const appResponse = await getAppliedJobs(email, 1, 1000);
      setJobApplications(appResponse.data || []);
    } catch (error) {
      console.error("Error fetching student applications:", error);
    }
  };

  // Fetch form data
  const fetchFormData = useCallback(async () => {
    if (courseOptions.length === 0 && !courseLoading) {
      await fetchCourses(false, "", 1);
    }
    if (deptOptions.length === 0 && !deptLoading) {
      await fetchDepartments(false, "", 1);
    }
    if (branchOptions.length === 0 && !branchLoading) {
      await fetchBranches(false, "", 1);
    }
  }, [
    courseOptions.length,
    deptOptions.length,
    branchOptions.length,
    courseLoading,
    deptLoading,
    branchLoading,
    fetchCourses,
    fetchDepartments,
    fetchBranches,
  ]);

  // Load form data when modal opens
  useEffect(() => {
    if (showModal && !modalDataFetchedRef.current) {
      modalDataFetchedRef.current = true;

      setCoursePage(1);
      setDeptPage(1);
      setBranchPage(1);
      setCourseSearch("");
      setDeptSearch("");
      setBranchSearch("");
      setCourseHasMore(true);
      setDeptHasMore(true);
      setBranchHasMore(true);

      fetchFormData();
    }

    if (!showModal) {
      modalDataFetchedRef.current = false;
    }
  }, [showModal, fetchFormData]);

  const fetchStudentApplicationInfo = useCallback(async () => {
    if (isAdmin) return;
    if (studentInfoFetchedRef.current) return;

    const studentEmail = currentStudentEmail;

    try {
      const response = await getStudentApplicationInfo(studentEmail);

      if (response && response.data) {
        const studentData = response.data;
        const billingData = studentData.studentFromBilling?.[0];

        if (billingData) {

          const departments = billingData.departmentIds || [];
          const branches = billingData.preferredBranchId || [];

          setPrefilledStudentData(prev => ({
            ...prev,
            studentName: billingData.name || prev.studentName,
            studentEmail: billingData.email || prev.studentEmail,
            studentPhoneNumber: billingData.phone || prev.studentPhoneNumber,
            studentDepartments: departments,
            studentBranches: branches,
          }));

          if (departments.length > 0) {
            setPrefilledFormData(prev => ({
              ...prev,
              departmentIds: departments.map(dept => dept.id),
            }));
          }

          if (branches.length > 0) {
            setPrefilledFormData(prev => ({
              ...prev,
              branchIds: branches.map(branch => branch.id),
            }));
          }
        }

        const lmsData = studentData.studentFromLMS;
        if (lmsData) {
          setPrefilledFormData(prev => ({
            ...prev,
            tenthMarks: lmsData.tenthMarks ? String(lmsData.tenthMarks) : prev.tenthMarks,
            twelfthMarks: lmsData.twelfthMarks ? String(lmsData.twelfthMarks) : prev.twelfthMarks,
          }));
        }

        studentInfoFetchedRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching student application info:", error);
    }
  }, [isAdmin, currentStudentEmail]);

  // Fetch student info when modal opens
  useEffect(() => {
    if (showModal && !isAdmin) {
      studentInfoFetchedRef.current = false;
      fetchStudentApplicationInfo();
    }
  }, [showModal, isAdmin, fetchStudentApplicationInfo]);

  // Fetch jobs based on selected tab
  const fetchJobsByTab = async (
    tab,
    page = 1,
    search = "",
    studentEmail = null,
    /* Passed explicitly by the page-size control, which fires before its
       own state has committed. */
    sizeOverride = null,
  ) => {
    const pageSize = sizeOverride || recordsPerPage;
    if (
      jobsFetchRef.current &&
      page === currentPage &&
      search === debouncedSearchTerm &&
      studentEmail === selectedStudentEmail
    ) {
      return;
    }

    jobsFetchRef.current = true;
    // A scroll batch already set loadingMore and keeps the rows on screen;
    // raising `loading` too would drop the table's overlay over rows the
    // reader is in the middle of, on every batch.
    // A fresh load draws skeleton rows, so the global overlay stays down.
    if (!appendRef.current) {
      silenceFor();
      setLoading(true);
    }

    try {
      let response;
      const effectiveStudentEmail = studentEmail || currentStudentEmail;

      switch (tab) {
        case "active":
          if (effectiveStudentEmail) {
            response = await getJobsForStudent(
              effectiveStudentEmail,
              "active",
              page,
              pageSize,
              search,
            );
          } else {
            response = await getJobsByStatus(
              "active",
              page,
              pageSize,
              search,
            );
          }
          break;

        case "applied":
          if (effectiveStudentEmail) {
            response = await getAppliedJobs(
              effectiveStudentEmail,
              page,
              pageSize,
              search,
            );
          } else {
            response = { data: [], pagination: { totalPages: 1, total: 0 } };
          }
          break;

        case "missed":
          if (effectiveStudentEmail) {
            response = await getMissedJobs(
              effectiveStudentEmail,
              page,
              pageSize,
              search,
            );
          } else {
            response = await getJobsByStatus(
              "inactive",
              page,
              pageSize,
              search,
            );
          }
          break;

        default:
          response = await getJobsByStatus(
            "active",
            page,
            pageSize,
            search,
          );
      }

      const jobsData = response.data || [];
      if (tab === "applied") {
        setJobApplications(jobsData);
      }
      const pagination = response.pagination || {};

      const totalPagesValue = pagination.totalPages || 1;
      const totalJobsValue = pagination.total || 0;

      /* A scroll batch adds to the list; anything else replaces it.
         This used to always replace, so page 2 wiped page 1 and the
         list could never grow past the first batch. The id guard drops
         any overlap a ragged page boundary produces. */
      setJobs((prev) => {
        if (page <= 1 || !appendRef.current) return jobsData;
        const seen = new Set(prev.map((j) => j.id));
        return [...prev, ...jobsData.filter((j) => !seen.has(j.id))];
      });
      appendRef.current = false;
      setTotalPages(totalPagesValue);
      setLoadingMore(false);
      setTotalJobs(totalJobsValue);
      setCurrentPage(page);

      if (!isAdmin && effectiveStudentEmail) {
        const cacheKey = getStudentCacheKey(
          tab,
          page,
          search,
          effectiveStudentEmail,
        );
        studentJobCache.set(cacheKey, {
          jobs: jobsData,
          totalPages: totalPagesValue,
          totalJobs: totalJobsValue,
          timestamp: Date.now(),
        });
      }

      return jobsData;
    } catch (error) {
      console.error(`Error fetching ${tab} jobs:`, error);
      toast.error(`Failed to fetch ${tab} jobs`);
      setJobs([]);
      setTotalPages(1);
      setTotalJobs(0);
      return [];
    } finally {
      setLoading(false);
      jobsFetchRef.current = false;
    }
  };

  // Fetch student data for current logged-in student
  const fetchCurrentStudentData = async (userObject) => {
    try {
      const userData = userObject?.user || userObject;
      if (!userData) return null;

      const adminStatus = checkIfAdmin(userData);
      setIsAdmin(adminStatus);

      let studentEmail = null;
      let studentId = null;

      if (!adminStatus) {
        // Optimized search - search by exact email
        const studentsResponse = await getAllStudentsFromStudentsAPI(
          1,
          1,
          userData.email,
        );
        const matchingStudent = studentsResponse.data?.find(
          (s) =>
            s.email && s.email.toLowerCase() === userData.email.toLowerCase(),
        );

        if (matchingStudent) {
          studentId = matchingStudent.id;
          studentEmail = matchingStudent.email;
          setCurrentStudentId(studentId);
          setCurrentStudentEmail(studentEmail);

          // Fetch applied jobs immediately to show "APPLIED" badges on job cards
          const appliedResponse = await getAppliedJobs(studentEmail, 1, 1000);
          setJobApplications(appliedResponse.data || []);

          setPrefilledStudentData({
            studentName: matchingStudent.name || "",
            studentEmail: matchingStudent.email || "",
            studentPhoneNumber: matchingStudent.phone || "",
            studentDepartments: [],
            studentBranches: [],
          });
        }
      }

      return { userData, adminStatus, studentEmail };
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to fetch user data");
      return null;
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    if (isAdmin && !selectedStudentEmail && tab !== "active") {
      toast.info(
        "Please select a student first to view applied or missed jobs",
      );
      return;
    }

    setSelectedTab(tab);
    setCurrentPage(1);
    jobsFetchRef.current = false;
    const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
    fetchJobsByTab(tab, 1, debouncedSearchTerm, studentEmail);
  };

  // Handle search with debounce
  useEffect(() => {
    // Skip if not initialized or if search term hasn't changed since last fetch
    if (initializationRef.current && debouncedSearchTerm !== lastSearchRef.current) {
      lastSearchRef.current = debouncedSearchTerm;
      setCurrentPage(1);
      jobsFetchRef.current = false;
      const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
      fetchJobsByTab(selectedTab, 1, debouncedSearchTerm, studentEmail);
    }
  }, [debouncedSearchTerm]);

  /* A new page size re-reads the list from the top: the batches already
     on screen were cut at the old size, so appending to them would
     double-count rows. */
  const handleRowsPerPageChange = (newLimit) => {
    const size = Number(newLimit) || DEFAULT_PAGE_SIZE;
    setRecordsPerPage(size);
    setCurrentPage(1);
    appendRef.current = false;
    jobsFetchRef.current = false;
    const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
    fetchJobsByTab(selectedTab, 1, debouncedSearchTerm, studentEmail, size);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
    fetchJobsByTab(selectedTab, newPage, debouncedSearchTerm, studentEmail);
  };

  // Check if student has applied for a job
  const hasStudentAppliedForJob = (studentId, jobProfileId) => {
    if (!jobProfileId) return false;
    // Since jobApplications is already filtered for the specific student, 
    // we only need to check if the jobProfileId matches any job in the list.
    return jobApplications.some(
      (application) =>
        application.jobProfileId === jobProfileId || application.id === jobProfileId,
    );
  };

  // Get filtered jobs for current view
  const filteredJobs = useMemo(() => {
    return jobs;
  }, [jobs]);

  /* Five columns and no more. Salary, description, the posted date and
     every action are in the popup the row opens. */
  const jobHeaders = [
    {
      title: "Role",
      prop: "jobName",
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-strong">{row.jobName || "—"}</span>,
    },
    {
      title: "Organisation",
      prop: "organizationName",
      searchable: true,
      sortable: true,
      cell: (row) => <span>{row.organizationName || "—"}</span>,
    },
    {
      title: "Location",
      prop: "jobLocation",
      sortable: true,
      cell: (row) =>
        row.jobLocation ? <span>{row.jobLocation}</span> : <span className="pg-none">—</span>,
    },
    {
      title: "Posted",
      prop: "jobPostedDate",
      sortable: true,
      cell: (row) => (
        <span className="pg-date">
          {row.jobPostedDate
            ? new Date(row.jobPostedDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      sortable: true,
      cell: (row) => (
        <span
          className={`pg-pill${
            String(row.status || "").toLowerCase() === "active" ? " pg-pill--live" : ""
          }`}
        >
          {row.status || "Active"}
        </span>
      ),
    },
  ];

  /* Infinite scroll, the shape ListAttendance uses: the page is the
     scroller and the foot of the list reaching the viewport asks the
     server for the next page.

     Below filteredJobs on purpose — it reads that const, and a hook
     above it would fail with "Cannot access before initialization".
     Neither eslint nor the bundler catches that. */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore) return;
      if (currentPage >= totalPages) return;
      /* Fetch, do not just move the page number — nothing watches
         currentPage, so bumping it alone sent no request and the list
         sat at its first batch. */
      appendRef.current = true;
      setLoadingMore(true);
      jobsFetchRef.current = false;
      const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
      fetchJobsByTab(selectedTab, currentPage + 1, debouncedSearchTerm, studentEmail);
    },
    [
      filteredJobs.length,
      currentPage,
      totalPages,
      loading,
      loadingMore,
      selectedTab,
      debouncedSearchTerm,
      isAdmin,
      selectedStudentEmail,
      currentStudentEmail,
    ],
  );

  // Get status counts
  const statusCounts = useMemo(() => {
    return {
      active: totalJobs,
      applied: 0,
      missed: 0,
    };
  }, [totalJobs]);

  // Check if tabs should be disabled for admin
  const isTabDisabled = (tab) => {
    if (!isAdmin) return false;
    if (tab === "active") return false;
    return !selectedStudentEmail;
  };

  // Get tooltip message for disabled tabs
  const getTabTooltip = (tab) => {
    if (tab === "applied")
      return "Please select a student first to see applied jobs";
    if (tab === "missed")
      return "Please select a student first to see missed jobs";
    return "";
  };

  // Initial load
  useEffect(() => {
    const initialize = async () => {
      if (initializationRef.current || !user) return;
      initializationRef.current = true;

      const result = await fetchCurrentStudentData(user);

      const effectiveAdminStatus = result?.adminStatus ?? isAdmin;
      const effectiveStudentEmail = result?.adminStatus
        ? selectedStudentEmail
        : (result?.studentEmail || currentStudentEmail);

      await fetchJobsByTab(
        selectedTab,
        currentPage,
        debouncedSearchTerm,
        effectiveStudentEmail,
      );
      lastSearchRef.current = debouncedSearchTerm;
    };

    initialize();
  }, [user]);

  // Handle apply click
  const handleApplyClick = (job) => {
    if (isAdmin) {
      toast.info(
        "Admin cannot apply for jobs. Please login as a student to apply.",
      );
      return;
    }
    setSelectedJob(job);
    setShowModal(true);
  };

  /* The popup opens at once on the list row, then fills in what the row
     does not carry: the description and the department's name (the list
     is summary rows -- `fields=summary`). Only the answer for the job still
     open is applied, so a quick second click is never overwritten. */
  const jdRequestSeq = useRef(0);
  const handleViewJDClick = async (job) => {
    const mine = ++jdRequestSeq.current;
    setSelectedJobForJD({ ...job, detailLoading: true });
    setShowJDModal(true);
    try {
      const res = await getJobById(job.id);
      if (jdRequestSeq.current !== mine) return;
      setSelectedJobForJD((prev) =>
        prev && prev.id === job.id ? { ...prev, ...(res?.data || {}), detailLoading: false } : prev,
      );
    } catch (_) {
      if (jdRequestSeq.current !== mine) return;
      setSelectedJobForJD((prev) => (prev && prev.id === job.id ? { ...prev, detailLoading: false } : prev));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Status colors
  const statusColors = {
    active: "bg-success",
    applied: "bg-primary",
    missed: "bg-warning",
  };

  const statusTabs = ["active", "applied", "missed"];

  // Filter courses for dropdowns
  const getFilteredCourses = (currentSelection, excludeSelections = []) => {
    return courseOptions.filter((course) => {
      if (course.value === currentSelection) return true;
      return !excludeSelections.includes(course.value);
    });
  };

  // Get department options
  const getDepartmentOptions = () => {
    return deptOptions;
  };

  // Get branch options
  const getBranchOptions = () => {
    return branchOptions;
  };

  // Get preselected departments (array of IDs)
  const getPreselectedDepartments = () => {
    if (!isAdmin && prefilledStudentData.studentDepartments?.length > 0) {
      return prefilledStudentData.studentDepartments.map(dept => dept.id);
    }
    if (prefilledFormData.departmentIds?.length > 0) {
      return prefilledFormData.departmentIds;
    }
    return [];
  };

  // Get preselected branches (array of IDs)
  const getPreselectedBranches = () => {
    if (!isAdmin && prefilledStudentData.studentBranches?.length > 0) {
      return prefilledStudentData.studentBranches.map(branch => branch.id);
    }
    if (prefilledFormData.branchIds?.length > 0) {
      return prefilledFormData.branchIds;
    }
    return [];
  };

  // Initial form values with arrays
  const getInitialValues = () => ({
    studentName: prefilledStudentData.studentName,
    studentEmail: prefilledStudentData.studentEmail,
    department: getPreselectedDepartments(),
    studentPhoneNumber: prefilledStudentData.studentPhoneNumber,
    technologiesLearned: "",
    addressWithPinCode: prefilledFormData.currentAddress || "",
    tenthPercentageAndYear: prefilledFormData.tenthMarks || "",
    twelfthPercentageAndYear: prefilledFormData.twelfthMarks || "",
    workExperience: "",
    course1: prefilledFormData.course1 || "",
    course2: prefilledFormData.course2 || "",
    course3: prefilledFormData.course3 || "",
    graduationPercentageAndYear: "",
    oneTimePlacementFee: "",
    selectBranch: getPreselectedBranches(),
    postGraduationPercentageAndYear: "",
    cvUpload: null,
    createdById: isAdmin ? selectedStudentId || "NA" : currentStudentId || "NA",
    updatedById: isAdmin ? selectedStudentId || "NA" : currentStudentId || "NA",
  });

  // Validation schema with arrays
  const validationSchema = Yup.object({
    studentName: Yup.string().required("Student Name is required"),
    studentEmail: Yup.string()
      .email("Invalid email address")
      .required("Student Email is required"),
    department: Yup.array()
      .min(1, "At least one department is required")
      .required("Department is required"),
    studentPhoneNumber: Yup.string().required(
      "Student Phone Number is required",
    ),
    technologiesLearned: Yup.string().required(
      "Technologies learned is required",
    ),
    addressWithPinCode: Yup.string().required(
      "Address with Pin Code is required",
    ),
    tenthPercentageAndYear: Yup.string().required(
      "10th class percentage and year is required",
    ),
    twelfthPercentageAndYear: Yup.string().required(
      "12th class percentage and year is required",
    ),
    workExperience: Yup.string(),
    course1: Yup.string().required("At least one course is required"),
    course2: Yup.string(),
    course3: Yup.string(),
    graduationPercentageAndYear: Yup.string().required(
      "Graduation percentage and year is required",
    ),
    oneTimePlacementFee: Yup.string().required(
      "One-Time Placement Fee is required",
    ),
    selectBranch: Yup.array()
      .min(1, "At least one branch is required")
      .required("Branch is required"),
    postGraduationPercentageAndYear: Yup.string(),
    cvUpload: Yup.mixed()
      .required("CV is required")
      .test(
        "fileFormat",
        "Only PDF, DOC, and DOCX files are allowed",
        (value) => {
          if (!value) return false;
          const supportedFormats = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ];
          return supportedFormats.includes(value.type);
        },
      )
      .test("fileSize", "File size must be less than 5MB", (value) => {
        if (!value) return false;
        return value.size <= 5 * 1024 * 1024;
      }),
  });

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const jobId = selectedJob?.id;
      if (!jobId) {
        throw new Error("Please select a job to apply for");
      }

      const studentIdForApplication = isAdmin
        ? selectedStudentId
        : currentStudentId;
      if (!studentIdForApplication) {
        throw new Error("Student ID not found for application");
      }

      const applicationData = {
        ...values,
        studentId: studentIdForApplication,
        jobProfileId: jobId,
        createdById: studentIdForApplication,
        updatedById: studentIdForApplication,
      };

      await submitJobApplicationData(applicationData, jobId);

      clearStudentCache();

      const studentEmail = isAdmin ? selectedStudentEmail : currentStudentEmail;
      await fetchJobsByTab(
        selectedTab,
        currentPage,
        debouncedSearchTerm,
        studentEmail,
      );

      // Refresh applied jobs list
      try {
        const appResponse = await getAppliedJobs(studentEmail, 1, 1000);
        setJobApplications(appResponse.data || []);
      } catch (error) {
        console.error("Error refreshing applications:", error);
      }

      toast.success("Your application has been submitted successfully!");
    } catch (error) {
      console.error("Form submission failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit application. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // A sent application is confirmed in the registration dialog itself,
  // which then closes on its own.
  const { done: applyDone } = usePopupDone(Boolean(showModal && selectedJob));

  return (
    <div className="pg">
      {/* Loading overlay */}
      {isSubmitting && (
        <div className="js-overlay">
          <div>
            <div className="js-spin js-accent" role="status">
              <span className="js-sr">Sending...</span>
            </div>
            <h5>Sending your application...</h5>
            <p className="js-muted">
              Please wait while we process your request.
            </p>
          </div>
        </div>
      )}

      {/* One filter bar: who is being read, what is being searched for,
          and how many rows a batch holds. Three cards stacked down the
          page before; the attendance list puts all of this on one row. */}
      <FilterSheet label="Filter jobs">
        <div className="pg-filters-row">
          {isAdmin && (
            <div className="pg-field pg-field--grow">
              <label className="pg-label">Viewing student</label>
              <NewCustomAsyncSelectDropdown
                options={studentOptions}
                value={
                  selectedStudentEmail
                    ? [{ value: selectedStudentEmail, label: selectedStudentName || selectedStudentEmail }]
                    : []
                }
                onChange={handleStudentSelection}
                placeholder="Search by name, email or phone…"
                isMulti={false}
                isSearchable={true}
                isLoading={studentOptionsLoading}
                onInputChange={handleStudentSearch}
                onMenuScrollToBottom={loadMoreStudents}
                onMenuOpen={handleStudentMenuOpen}
                isClearable
              />
            </div>
          )}

          <div className="pg-field pg-field--search pg-field--grow">
            <label className="pg-label" htmlFor="job-search">
              Search
            </label>
            <input
              id="job-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Role or organisation…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Rows per batch, beside the search as asked. */}
          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="job-rows">
              Rows
            </label>
            <select
              id="job-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              {PAGE_SIZE_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FilterSheet>

      {/* Navigation Tabs

          These carried their whole appearance as inline style: an orange
          gradient for the selected tab, an orange glow, a scale
          transform, and three mouse handlers repainting them on hover.
          Inline style beats the stylesheet, so none of the theme applied
          and the strip stayed orange. It is the .js-tab / .is-on classes
          now, in the house blue, and the hover is CSS. */}
      <ul className="js-tabs">
        {statusTabs.map((tab) => {
          const isDisabled = isTabDisabled(tab);
          const tooltip = getTabTooltip(tab);

          return (
            <li className="js-tab-item" key={tab} title={isDisabled ? tooltip : ""}>
              <button
                type="button"
                className={`js-tab${selectedTab === tab ? " is-on" : ""}`}
                onClick={() => !isDisabled && handleTabChange(tab)}
                disabled={isDisabled}
                aria-selected={selectedTab === tab}
              >
                <span className="js-hide-sm">
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </span>

                {statusCounts[tab] > 0 && (
                  <span className="js-badge">{statusCounts[tab]}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Loading State */}
      {/* Rows of the job list, shimmering, where the list will be. */}
      {loading && (
        <SkeletonGroup label="Loading jobs" className="lms-skel-stack">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton shape="row" key={i} />
          ))}
        </SkeletonGroup>
      )}

      {/* Job Listings — the shared list, five columns, row opens the
          record. Everything else about a job is in that popup. */}
      {!loading && (
        <>
          {filteredJobs.length === 0 ? (
            <div className="pg-state">
              <span className="pg-state-icon">
                <i />
              </span>
              <h3 className="pg-state-title">No jobs found</h3>
              <p className="pg-state-text">
                {isAdmin && !selectedStudentEmail && selectedTab !== "active"
                  ? "Select a student to see the jobs shared with them."
                  : searchTerm
                    ? "No jobs match your current filters."
                    : "Nothing has been shared here yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="td-list td-list--job" ref={listRef}>
                <NewCustomDataTable
                  data={filteredJobs}
                  headers={jobHeaders}
                  loading={loading}
                  loadingMore={loadingMore}
                  showToolbar={false}
                  showPagination={false}
                  showFooter={false}
                  onRowClick={(row) => handleViewJDClick(row)}
                  /* Every cell wraps its text in a span, so the default
                     ['TR','TD'] would leave the text unclickable. */
                  validRowClickTagNames="*"
                  rowProps={(row) => ({
                    tabIndex: 0,
                    role: "button",
                    "aria-label": `Open ${row.jobName || "this job"}`,
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleViewJDClick(row);
                      }
                    },
                  })}
                />
              </div>

              {/* What the page buttons used to say, said once. */}
              <div className="pg-scroll-foot" ref={listEndRef}>
                {filteredJobs.length < totalJobs ? (
                  <>
                    <span className="pg-scroll-count">
                      {filteredJobs.length} of {totalJobs}
                    </span>
                    {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
                  </>
                ) : (
                  totalJobs > 0 && (
                    <span className="pg-scroll-count">
                      All {totalJobs} job{totalJobs === 1 ? "" : "s"} loaded
                    </span>
                  )
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* The record popup. Everything the five columns leave out. */}
      {showJDModal && selectedJobForJD && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="jd-title"
          onClick={() => setShowJDModal(false)}
        >
          <div
            className="pg-modal-panel pg-modal-panel--wide"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pgm-head">
              <div className="pgm-head-main">
                <h2 className="pgm-batch" id="jd-title">
                  {selectedJobForJD.jobName || "Job"}
                </h2>
                <dl className="pgm-ident">
                  <div className="pgm-ident-row">
                    <dt>Organisation</dt>
                    <dd>{selectedJobForJD.organizationName || "—"}</dd>
                  </div>
                  <div className="pgm-ident-row">
                    <dt>Location</dt>
                    <dd>{selectedJobForJD.jobLocation || "—"}</dd>
                  </div>
                  <div className="pgm-ident-row">
                    <dt>Status</dt>
                    <dd>
                      {/* Colour is a tag, never the figure. */}
                      <span
                        className={`pgm-pill${
                          String(selectedJobForJD.status || "Active").toLowerCase() === "active"
                            ? " pgm-pill--good"
                            : ""
                        }`}
                      >
                        {selectedJobForJD.status || "Active"}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setShowJDModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {/* Ink figures; the status is the pill in the head. */}
              <div className="pgm-tiles">
                <div className="pgm-tile">
                  <span>Salary</span>
                  <b>{selectedJobForJD.salary || "—"}</b>
                </div>
                <div className="pgm-tile">
                  <span>Department</span>
                  {/* The name, resolved by the API -- `department` itself holds ids. */}
                  <b>{selectedJobForJD.departmentName || "—"}</b>
                </div>
                <div className="pgm-tile">
                  <span>Position status</span>
                  <b>{selectedJobForJD.jobPositionStatus || "—"}</b>
                </div>
              </div>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Dates</h3>
                <dl className="pgm-facts">
                  <div className="pgm-fact">
                    <dt>Posted</dt>
                    <dd>
                      {selectedJobForJD.jobPostedDate
                        ? new Date(selectedJobForJD.jobPostedDate).toLocaleDateString(
                            "en-GB",
                            { day: "numeric", month: "short", year: "numeric" },
                          )
                        : "—"}
                    </dd>
                  </div>
                  <div className="pgm-fact">
                    <dt>Apply by</dt>
                    <dd>
                      {selectedJobForJD.endDate
                        ? new Date(selectedJobForJD.endDate).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </dd>
                  </div>
                </dl>
              </section>

              {selectedJobForJD.detailLoading && !selectedJobForJD.jobDescription && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Job description</h3>
                  <p className="pgm-empty">Loading…</p>
                </section>
              )}
              {selectedJobForJD.jobDescription && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Job description</h3>
                  {/* The description arrives as HTML from the job form. */}
                  <div
                    className="js-jd-body"
                    dangerouslySetInnerHTML={safeHtml(selectedJobForJD.jobDescription)}
                  />
                </section>
              )}
            </div>

            {/* The reference's order: what you can do with the record on
                the left, Close alone on the right as the filled button. */}
            <footer className="pg-modal-foot">
              {selectedTab === "active" && (
                <div className="pgm-actions">
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      setShowJDModal(false);
                      handleApplyClick(selectedJobForJD);
                    }}
                  >
                    Apply now
                  </button>
                </div>
              )}
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setShowJDModal(false)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Application Modal */}
      {showModal && selectedJob && (
        /* This was styled entirely inline: its own fixed positioning,
           its own backdrop, an orange heading, and overflow:hidden on
           the panel — which clips the async dropdown menus inside the
           form. Inline style beats the stylesheet, so none of the shared
           modal rules applied. It is the pg-modal classes now.

           The backdrop check also tested for "modal-overlay", a class
           this file stopped using, so clicking outside never closed it. */
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowModal(false);
            setSelectedJob(null);
          }}
        >
          {/* --xl alone: its body scrolls and the footer stays put. --form
              is for a body that hands scrolling to an inner stage, which
              this form does not have. */}
          <div
            className="pg-modal-panel pg-modal-panel--xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pgm-head">
              <div className="pgm-head-main">
                <h2 className="pgm-batch" id="js-apply-title">
                  Placement registration
                </h2>
                <p className="pgm-sub">
                  {selectedJob.jobName}
                  {selectedJob.organizationName ? ` · ${selectedJob.organizationName}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                aria-label="Close"
                onClick={() => {
                  setShowModal(false);
                  setSelectedJob(null);
                }}
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body js-modal-body">
              {applyDone ? (
                <PopupDone
                  message={applyDone}
                  onClose={() => {
                    setShowModal(false);
                    setSelectedJob(null);
                  }}
                />
              ) : (
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, setFieldTouched, values }) => (
                  <Form id="js-apply-form" aria-labelledby="js-apply-title">
                    <div className="js-container">
                      {/* Row 1: Student Name and Email */}
                      <div className="js-row">
                        <div className="js-col-6">
                          <TextInput label="Student Name" name="studentName" disabled={true} />
                        </div>
                        <div className="js-col-6">
                          <TextInput
                            label="Student Email"
                            name="studentEmail"
                            type="email"
                            disabled={true}
                          />
                        </div>
                      </div>

                      {/* Row 2: Department and Phone - Multi-select for departments */}
                      <div className="js-row">
                        <div className="js-col-6">
                          <label className="js-label">Department</label>
                          <NewCustomAsyncSelectDropdown
                            options={getDepartmentOptions()}
                            value={(() => {
                              // For non-admin students with fetched departments
                              if (!isAdmin && prefilledStudentData.studentDepartments?.length > 0) {
                                const selectedDepartments = prefilledStudentData.studentDepartments
                                  .map(dept => {
                                    const matchedOption = deptOptions.find(opt => opt.value === dept.id);
                                    return matchedOption;
                                  })
                                  .filter(Boolean);
                                return selectedDepartments;
                              }
                              // For admin or when no prefilled data, return selected departments from form values
                              if (values.department && values.department.length > 0 && deptOptions.length > 0) {
                                const selectedDepartments = values.department
                                  .map(deptId => {
                                    const matchedOption = deptOptions.find(opt => opt.value === deptId);
                                    return matchedOption;
                                  })
                                  .filter(Boolean);
                                return selectedDepartments;
                              }
                              return [];
                            })()}
                            onChange={(selectedOptions) => {
                              // For non-admin with prefilled departments, prevent changes
                              if (!isAdmin && prefilledStudentData.studentDepartments?.length > 0) {
                                return;
                              }

                              // Store all selected department IDs as an array
                              if (selectedOptions && selectedOptions.length > 0) {
                                const selectedDepartmentIds = selectedOptions.map(opt => opt.value);
                                setFieldValue("department", selectedDepartmentIds);
                              } else {
                                setFieldValue("department", []);
                              }
                            }}
                            placeholder="Select the Department"
                            onMenuOpen={() => {
                              if (deptOptions.length === 0 && !deptLoading) {
                                fetchDepartments(false, "", 1);
                              }
                            }}
                            onRefresh={() => {
                              setDeptSearch("");
                              setDeptPage(1);
                              setDeptHasMore(true);
                              fetchDepartments(false, "", 1);
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue !== deptSearch) {
                                setDeptSearch(inputValue);
                                setDeptPage(1);
                                setDeptHasMore(true);
                                fetchDepartments(false, inputValue, 1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                deptHasMore &&
                                !deptLoading &&
                                deptOptions.length > 0
                              ) {
                                fetchDepartments(
                                  true,
                                  deptSearch,
                                  deptPage + 1,
                                );
                              }
                            }}
                            isLoading={deptLoading}
                            isMulti={true}
                            isSearchable={true}
                            isDisabled={!isAdmin && prefilledStudentData.studentDepartments?.length > 0}
                          />

                        </div>
                        <div className="js-col-6">
                          <TextInput
                            label="Student Phone Number"
                            name="studentPhoneNumber"
                            disabled={true}
                          />
                        </div>
                      </div>

                      {/* Row 3: Technologies Learned */}
                      <div className="js-row">
                        <div className="js-col-12">
                          <TextAreaInput
                            label="Technologies which you have learned"
                            name="technologiesLearned"
                            placeholder="Enter technologies you have learned..."
                            rows={3}
                          />
                        </div>
                      </div>

                      {/* Row 4: Address */}
                      <div className="js-row">
                        <div className="js-col-12">
                          <TextAreaInput
                            label="Address with Pin Code"
                            name="addressWithPinCode"
                            placeholder="Enter your complete address with pin code..."
                            rows={3}
                          />
                        </div>
                      </div>

                      {/* Row 5: 10th and 12th */}
                      <div className="js-row">
                        <div className="js-col-6">
                          <TextInput
                            label="10th class percentage and Year of Passing"
                            name="tenthPercentageAndYear"
                          />
                        </div>
                        <div className="js-col-6">
                          <TextInput
                            label="12th class percentage and Year of Passing"
                            name="twelfthPercentageAndYear"
                          />
                        </div>
                      </div>

                      {/* Row 6: Work Experience */}
                      <div className="js-row">
                        <div className="js-col-12">
                          <TextAreaInput
                            label="Work Experience (If Any)"
                            name="workExperience"
                            placeholder="Enter your work experience if any..."
                            rows={3}
                          />
                        </div>
                      </div>

                      {/* Row 7: Courses */}
                      <div className="js-row">
                        <div className="js-col-4">
                          <label className="js-label">Course 1</label>
                          <NewCustomAsyncSelectDropdown
                            options={getFilteredCourses(values.course1, [
                              values.course2,
                              values.course3,
                            ])}
                            value={(() => {
                              if (values.course1 && courseOptions.length > 0) {
                                const selected = courseOptions.find(
                                  (opt) => opt.value === values.course1,
                                );
                                return selected ? [selected] : [];
                              }
                              return [];
                            })()}
                            onChange={(selectedOptions) => {
                              const selected =
                                selectedOptions && selectedOptions.length > 0
                                  ? selectedOptions[0]
                                  : null;
                              setFieldValue(
                                "course1",
                                selected ? selected.value : "",
                              );
                            }}
                            placeholder="Select the course"
                            onMenuOpen={() => {
                              if (
                                courseOptions.length === 0 &&
                                !courseLoading
                              ) {
                                fetchCourses(false, "", 1);
                              }
                            }}
                            onRefresh={() => {
                              setCourseSearch("");
                              setCoursePage(1);
                              setCourseHasMore(true);
                              fetchCourses(false, "", 1);
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue !== courseSearch) {
                                setCourseSearch(inputValue);
                                setCoursePage(1);
                                setCourseHasMore(true);
                                fetchCourses(false, inputValue, 1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                courseHasMore &&
                                !courseLoading &&
                                courseOptions.length > 0
                              ) {
                                fetchCourses(
                                  true,
                                  courseSearch,
                                  coursePage + 1,
                                );
                              }
                            }}
                            isLoading={courseLoading}
                            isMulti={false}
                            isSearchable={true}
                          />
                        </div>

                        <div className="js-col-4">
                          <label className="js-label">Course 2</label>
                          <NewCustomAsyncSelectDropdown
                            options={getFilteredCourses(values.course2, [
                              values.course1,
                              values.course3,
                            ])}
                            value={(() => {
                              if (values.course2 && courseOptions.length > 0) {
                                const selected = courseOptions.find(
                                  (opt) => opt.value === values.course2,
                                );
                                return selected ? [selected] : [];
                              }
                              return [];
                            })()}
                            onChange={(selectedOptions) => {
                              const selected =
                                selectedOptions && selectedOptions.length > 0
                                  ? selectedOptions[0]
                                  : null;
                              setFieldValue(
                                "course2",
                                selected ? selected.value : "",
                              );
                            }}
                            placeholder="Select the course"
                            onMenuOpen={() => {
                              if (
                                courseOptions.length === 0 &&
                                !courseLoading
                              ) {
                                fetchCourses(false, "", 1);
                              }
                            }}
                            onRefresh={() => {
                              setCourseSearch("");
                              setCoursePage(1);
                              setCourseHasMore(true);
                              fetchCourses(false, "", 1);
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue !== courseSearch) {
                                setCourseSearch(inputValue);
                                setCoursePage(1);
                                setCourseHasMore(true);
                                fetchCourses(false, inputValue, 1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                courseHasMore &&
                                !courseLoading &&
                                courseOptions.length > 0
                              ) {
                                fetchCourses(
                                  true,
                                  courseSearch,
                                  coursePage + 1,
                                );
                              }
                            }}
                            isLoading={courseLoading}
                            isMulti={false}
                            isSearchable={true}
                          />
                        </div>
                        <div className="js-col-4">
                          <label className="js-label">Course 3</label>
                          <NewCustomAsyncSelectDropdown
                            options={getFilteredCourses(values.course3, [
                              values.course1,
                              values.course2,
                            ])}
                            value={(() => {
                              if (values.course3 && courseOptions.length > 0) {
                                const selected = courseOptions.find(
                                  (opt) => opt.value === values.course3,
                                );
                                return selected ? [selected] : [];
                              }
                              return [];
                            })()}
                            onChange={(selectedOptions) => {
                              const selected =
                                selectedOptions && selectedOptions.length > 0
                                  ? selectedOptions[0]
                                  : null;
                              setFieldValue(
                                "course3",
                                selected ? selected.value : "",
                              );
                            }}
                            placeholder="Select the course"
                            onMenuOpen={() => {
                              if (
                                courseOptions.length === 0 &&
                                !courseLoading
                              ) {
                                fetchCourses(false, "", 1);
                              }
                            }}
                            onRefresh={() => {
                              setCourseSearch("");
                              setCoursePage(1);
                              setCourseHasMore(true);
                              fetchCourses(false, "", 1);
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue !== courseSearch) {
                                setCourseSearch(inputValue);
                                setCoursePage(1);
                                setCourseHasMore(true);
                                fetchCourses(false, inputValue, 1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                courseHasMore &&
                                !courseLoading &&
                                courseOptions.length > 0
                              ) {
                                fetchCourses(
                                  true,
                                  courseSearch,
                                  coursePage + 1,
                                );
                              }
                            }}
                            isLoading={courseLoading}
                            isMulti={false}
                            isSearchable={true}
                          />
                        </div>
                      </div>

                      {/* Row 8: Graduation */}
                      <div className="js-row">
                        <div className="js-col-6">
                          <TextInput
                            label="Graduation-Degree Percentage & year of Passing"
                            name="graduationPercentageAndYear"
                          />
                        </div>
                        <div className="js-col-6">
                          <TextInput
                            label="Post Graduation Percentage & year of Passing"
                            name="postGraduationPercentageAndYear"
                          />
                        </div>
                      </div>

                      {/* Row 9: Placement Fee and Branch - Multi-select for branches */}
                      <div className="js-row">
                        <div className="js-col-6">
                          <label className="js-label">
                            One-Time Placement Fee
                          </label>
                          <NewCustomAsyncSelectDropdown
                            options={[
                              { value: "yes", label: "Yes - Will Pay" },
                              { value: "no", label: "No - Won't Pay" },
                            ]}
                            value={
                              values.oneTimePlacementFee
                                ? [
                                  {
                                    value: values.oneTimePlacementFee,
                                    label:
                                      values.oneTimePlacementFee === "yes"
                                        ? "Yes - Will Pay"
                                        : "No - Won't Pay",
                                  },
                                ]
                                : []
                            }
                            onChange={(selectedOptions) => {
                              const selected =
                                selectedOptions && selectedOptions.length > 0
                                  ? selectedOptions[0]
                                  : null;
                              setFieldValue(
                                "oneTimePlacementFee",
                                selected ? selected.value : "",
                              );
                            }}
                            placeholder="Select the option"
                            isMulti={false}
                            isSearchable={true}
                          />
                        </div>
                        <div className="js-col-6">
                          <label className="js-label">Select Branch</label>
                          <NewCustomAsyncSelectDropdown
                            options={getBranchOptions()}
                            value={(() => {
                              // For non-admin students with fetched branches
                              if (!isAdmin && prefilledStudentData.studentBranches?.length > 0) {
                                const selectedBranches = prefilledStudentData.studentBranches
                                  .map(branch => {
                                    const matchedOption = branchOptions.find(opt => opt.value === branch.id);
                                    return matchedOption;
                                  })
                                  .filter(Boolean);
                                return selectedBranches;
                              }
                              // For admin or when no prefilled data, return selected branches from form values
                              if (values.selectBranch && values.selectBranch.length > 0 && branchOptions.length > 0) {
                                const selectedBranches = values.selectBranch
                                  .map(branchId => {
                                    const matchedOption = branchOptions.find(opt => opt.value === branchId);
                                    return matchedOption;
                                  })
                                  .filter(Boolean);
                                return selectedBranches;
                              }
                              return [];
                            })()}
                            onChange={(selectedOptions) => {
                              // For non-admin with prefilled branches, prevent changes
                              if (!isAdmin && prefilledStudentData.studentBranches?.length > 0) {
                                return;
                              }

                              // Store all selected branch IDs as an array
                              if (selectedOptions && selectedOptions.length > 0) {
                                const selectedBranchIds = selectedOptions.map(opt => opt.value);
                                setFieldValue("selectBranch", selectedBranchIds);
                              } else {
                                setFieldValue("selectBranch", []);
                              }
                            }}
                            placeholder="Select the Branch"
                            onMenuOpen={() => {
                              if (
                                branchOptions.length === 0 &&
                                !branchLoading
                              ) {
                                fetchBranches(false, "", 1);
                              }
                            }}
                            onRefresh={() => {
                              setBranchSearch("");
                              setBranchPage(1);
                              setBranchHasMore(true);
                              fetchBranches(false, "", 1);
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue !== branchSearch) {
                                setBranchSearch(inputValue);
                                setBranchPage(1);
                                setBranchHasMore(true);
                                fetchBranches(false, inputValue, 1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                branchHasMore &&
                                !branchLoading &&
                                branchOptions.length > 0
                              ) {
                                fetchBranches(
                                  true,
                                  branchSearch,
                                  branchPage + 1,
                                );
                              }
                            }}
                            isLoading={branchLoading}
                            isMulti={true}
                            isSearchable={true}
                            isDisabled={!isAdmin && prefilledStudentData.studentBranches?.length > 0}
                          />
                          {!isAdmin && Array.isArray(prefilledStudentData.studentBranches) && prefilledStudentData.studentBranches.length > 1 && (
                            <small className="js-muted">
                              Available branches: {prefilledStudentData.studentBranches.map(b => b.name).join(", ")}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* Row 10: CV Upload. The /user-profile resume picker's
                          look (up-drop / up-resume): an empty drop panel with
                          one button, then the chosen file as a row. */}
                      <div className="js-row">
                        <div className="js-col-12">
                          <span className="js-label" id="js-cv-label">CV upload</span>
                          {values.cvUpload ? (
                            <div className="js-file" aria-labelledby="js-cv-label">
                              <span className="js-file-mark">
                                <FileText size={18} aria-hidden="true" />
                              </span>
                              <div className="js-file-main">
                                <div className="js-file-name">{values.cvUpload.name}</div>
                                <p className="js-file-sub">
                                  {values.cvUpload.size >= 1024 * 1024
                                    ? `${(values.cvUpload.size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${Math.max(1, Math.round(values.cvUpload.size / 1024))} KB`}
                                </p>
                              </div>
                              <div className="js-file-actions">
                                <label className="pgm-btn" htmlFor="js-cv-input">
                                  <Upload size={13} aria-hidden="true" />
                                  Replace
                                </label>
                                <button
                                  type="button"
                                  className="pgm-btn pgm-btn--danger"
                                  onClick={() => setFieldValue("cvUpload", null)}
                                >
                                  <X size={13} aria-hidden="true" />
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="js-drop">
                              <span className="js-drop-mark">
                                <FileText size={26} aria-hidden="true" />
                              </span>
                              <h3 className="js-drop-title">Attach your CV</h3>
                              <p className="js-drop-text">PDF, DOC or DOCX, up to 5 MB.</p>
                              <label className="pg-btn pg-btn--main" htmlFor="js-cv-input">
                                <Upload size={15} aria-hidden="true" />
                                Choose file
                              </label>
                            </div>
                          )}
                          {/* One input behind both states, driven by its labels. */}
                          <input
                            className="js-file-input"
                            id="js-cv-input"
                            name="cvUpload"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            aria-labelledby="js-cv-label"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              // Cleared so choosing the same file again still fires.
                              e.target.value = "";
                              if (!file) return;
                              setFieldValue("cvUpload", file);
                              setFieldTouched("cvUpload", true, false);
                            }}
                          />
                          <ErrorMessage name="cvUpload" component="div" className="error-message" />
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
              )}
            </div>

            {/* Outside the form, so it stays in view while the body
                scrolls; `form=` still submits it. */}
            {!applyDone && (
            <footer className="pg-modal-foot">
              <div className="pg-modal-foot-acts">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedJob(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="js-apply-form"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting…" : "Submit application"}
                </button>
              </div>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
